import React from 'react';
import { Check, X } from 'lucide-react';

const FeatureMatrix = ({ currentPlan }) => {
  const features = [
    {
      category: 'Symptom Tracking',
      items: [
        { name: 'Daily check-ins', basic: true, basicUltra: true, premium: true, family: true },
        { name: 'Symptom history (15 days)', basic: false, basicUltra: true, premium: false, family: false },
        { name: 'Symptom history (30 days)', basic: true, basicUltra: false, premium: false, family: false },
        { name: 'Symptom history (6 months)', basic: false, basicUltra: false, premium: true, family: true },
        { name: 'Unlimited symptom entries', basic: false, basicUltra: false, premium: true, family: true },
        { name: 'Photo attachments', basic: true, basicUltra: true, premium: true, family: true },
        { name: 'Voice notes', basic: false, basicUltra: false, premium: true, family: true }
      ]
    },
    {
      category: 'Analytics & Insights',
      items: [
        { name: 'Basic symptom charts', basic: true, basicUltra: true, premium: true, family: true },
        { name: 'Advanced analytics', basic: false, basicUltra: false, premium: true, family: true },
        { name: 'Pattern recognition', basic: false, basicUltra: false, premium: true, family: true },
        { name: 'Correlation analysis', basic: false, basicUltra: false, premium: true, family: true },
        { name: 'Predictive insights', basic: false, basicUltra: false, premium: true, family: true },
        { name: 'Custom reports', basic: false, basicUltra: false, premium: true, family: true }
      ]
    },
    {
      category: 'Exports & Sharing',
      items: [
        { name: 'Monthly exports (2)', basic: true, basicUltra: false, premium: false, family: false },
        { name: 'Monthly exports (5)', basic: false, basicUltra: true, premium: false, family: false },
        { name: 'Unlimited exports', basic: false, basicUltra: false, premium: true, family: true },
        { name: 'PDF format', basic: true, basicUltra: true, premium: true, family: true },
        { name: 'CSV & Word formats', basic: false, basicUltra: false, premium: true, family: true },
        { name: 'Doctor-ready reports', basic: false, basicUltra: false, premium: true, family: true },
        { name: 'Custom date ranges', basic: false, basicUltra: false, premium: true, family: true }
      ]
    },
    {
      category: 'Caregiver Features',
      items: [
        { name: 'Caregiver connections (1)', basic: true, basicUltra: false, premium: false, family: false },
        { name: 'No caregiver connections', basic: false, basicUltra: true, premium: false, family: false },
        { name: 'Unlimited caregivers', basic: false, basicUltra: false, premium: true, family: true },
        { name: 'Real-time alerts', basic: false, basicUltra: false, premium: true, family: true },
        { name: 'Shared dashboard', basic: false, basicUltra: false, premium: false, family: true },
        { name: 'Activity timeline', basic: false, basicUltra: false, premium: true, family: true },
        { name: 'Family management', basic: false, basicUltra: false, premium: false, family: true }
      ]
    },
    {
      category: 'Support & Security',
      items: [
        { name: 'Email support (48h)', basic: true, basicUltra: true, premium: false, family: false },
        { name: 'Priority support (2h)', basic: false, basicUltra: false, premium: true, family: true },
        { name: 'Live chat', basic: false, basicUltra: false, premium: true, family: true },
        { name: 'Dedicated account manager', basic: false, basicUltra: false, premium: false, family: true },
        { name: 'Two-factor authentication', basic: true, basicUltra: true, premium: true, family: true },
        { name: 'Audit trail', basic: false, basicUltra: false, premium: true, family: true }
      ]
    }
  ];

  return (
    <div className="bg-card rounded-xl border border-border shadow-therapeutic overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-muted/50">
              <th className="text-left p-4 font-heading font-bold text-foreground">Features</th>
              <th className="text-center p-4 font-heading font-bold text-foreground">
                Basic
                {currentPlan === 'basic' && (
                  <span className="block text-xs text-success font-normal mt-1">Current</span>
                )}
              </th>
              <th className="text-center p-4 font-heading font-bold text-foreground bg-blue-500/10">
                Basic Ultra
                {currentPlan === 'basic_ultra' && (
                  <span className="block text-xs text-success font-normal mt-1">Current</span>
                )}
              </th>
              <th className="text-center p-4 font-heading font-bold text-foreground bg-primary/5">
                Premium
                {currentPlan === 'premium' && (
                  <span className="block text-xs text-success font-normal mt-1">Current</span>
                )}
              </th>
              <th className="text-center p-4 font-heading font-bold text-foreground">
                Family
                {currentPlan === 'family' && (
                  <span className="block text-xs text-success font-normal mt-1">Current</span>
                )}
              </th>
            </tr>
          </thead>
          <tbody>
            {features?.map((category, categoryIndex) => (
              <React.Fragment key={categoryIndex}>
                <tr className="bg-muted/30">
                  <td colSpan={5} className="p-3 font-semibold text-sm text-foreground">
                    {category?.category}
                  </td>
                </tr>
                {category?.items?.map((item, itemIndex) => (
                  <tr key={itemIndex} className="border-t border-border hover:bg-muted/20 transition-colors">
                    <td className="p-4 text-sm text-foreground">{item?.name}</td>
                    <td className="p-4 text-center">
                      {item?.basic ? (
                        <Check className="w-5 h-5 text-success mx-auto" />
                      ) : (
                        <X className="w-5 h-5 text-muted-foreground mx-auto" />
                      )}
                    </td>
                    <td className="p-4 text-center bg-blue-500/10">
                      {item?.basicUltra ? (
                        <Check className="w-5 h-5 text-success mx-auto" />
                      ) : (
                        <X className="w-5 h-5 text-muted-foreground mx-auto" />
                      )}
                    </td>
                    <td className="p-4 text-center bg-primary/5">
                      {item?.premium ? (
                        <Check className="w-5 h-5 text-success mx-auto" />
                      ) : (
                        <X className="w-5 h-5 text-muted-foreground mx-auto" />
                      )}
                    </td>
                    <td className="p-4 text-center">
                      {item?.family ? (
                        <Check className="w-5 h-5 text-success mx-auto" />
                      ) : (
                        <X className="w-5 h-5 text-muted-foreground mx-auto" />
                      )}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FeatureMatrix;